/*
  ef-cart.js — cart drawer behaviour we own.

  Two jobs, both small:
    1. Print a header row above each bundle group. CSS cannot reach across sibling <tr>s to do
       this, and restructuring Dawn's cart table to nest rows is invasive and easy to break, so
       the header is injected instead.
    2. Re-run after the cart re-renders. Dawn replaces the cart contents wholesale via the
       Section Rendering API, which destroys anything we added.

  WHAT THIS DELIBERATELY DOES NOT DO.

  It does not wrap window.fetch. The old ef-preorder-stamp.js had to, because Kaching owned the
  cart and line-item properties were the only way in. We own this markup, so everything that can
  be rendered server-side in Liquid is rendered server-side. Measured on 2026-09-17: Kaching
  Cart's own fetch wrapper still installs even when its cart is disabled, so adding a second
  wrapper on top would be two layers of interception around the one code path that writes the
  basket. Don't.

  Spec: Doc/Ecomm/EcoFuego_Cart_Build_Spec.md
*/
(function () {
  'use strict';

  var HEAD_CLASS = 'ef-bundle-head';

  function clearHeaders(root) {
    var old = root.querySelectorAll('tr.' + HEAD_CLASS);
    for (var i = 0; i < old.length; i++) old[i].remove();
  }

  function columnCount(row) {
    // colSpan matters: without it the header cell collapses the table layout.
    var n = 0;
    for (var i = 0; i < row.cells.length; i++) n += row.cells[i].colSpan || 1;
    return n || 4;
  }


  function groupRows(root, id) {
    if (!id) return [];
    return [].slice.call(root.querySelectorAll('tr[data-ef-bundle="' + id.replace(/"/g, '') + '"]'));
  }

  function unitQty(row) {
    return Math.max(1, parseInt(row.getAttribute('data-ef-unit-qty'), 10) || 1);
  }

  /*
    "1 x El Campo Fire Pit + 2 x Wax Refills", read off the rows themselves. Built from the DOM
    rather than from a stored string so it can never disagree with what is in the basket.
  */
  function describe(rows) {
    // Read in the order the bundle was BUILT, not the order Shopify happens to list the lines.
    var ordered = rows.slice().sort(function (a, b) {
      return (parseInt(a.getAttribute('data-ef-bundle-pos'), 10) || 99) -
             (parseInt(b.getAttribute('data-ef-bundle-pos'), 10) || 99);
    });
    var parts = [];
    for (var i = 0; i < ordered.length; i++) {
      var nameEl = ordered[i].querySelector('.cart-item__name');
      if (!nameEl) continue;
      parts.push(unitQty(ordered[i]) + ' \u00d7 ' + nameEl.textContent.trim());
    }
    return parts.join('  +  ');
  }

  function bundleCount(row) {
    return Math.max(1, parseInt(row.getAttribute('data-ef-bundle-count'), 10) || 1);
  }

  /*
    Changing the bundle count rewrites EVERY line in the group in one /cart/update.js, so the
    group can never be left half-updated. Shopify's update endpoint takes a map keyed on line
    key and applies the whole map atomically, which is exactly the shape this needs.
  */
  function setBundleCount(rows, count) {
    var updates = {};
    for (var i = 0; i < rows.length; i++) {
      var key = rows[i].getAttribute('data-ef-line-key');
      if (key) updates[key] = unitQty(rows[i]) * count;
    }
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates, sections: 'cart-drawer,cart-icon-bubble' })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        var html = state && state.sections && state.sections['cart-drawer'];
        if (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.getElementById('CartDrawer-CartItems');
          var live = document.getElementById('CartDrawer-CartItems');
          if (fresh && live) live.innerHTML = fresh.innerHTML;
        }
        document.dispatchEvent(new CustomEvent('ef:cart:updated'));
      })
      .catch(function () { window.location.reload(); });  // never leave a stale basket on screen
  }

  /*
    ONE remove for the whole bundle. A per-line bin inside a bundle offers an action whose result
    is not what it looks like: deleting the fuel out of a Duo does not leave you with a cheaper
    Duo, it leaves a loose fire pit that no longer qualifies for anything. Removing the group is
    the honest control, and dissolveOrphans() below handles the case where a line leaves anyway.
  */
  function buildRemove(rows) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ef-bundle-remove';
    b.setAttribute('aria-label', 'Remove this bundle');
    b.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">'
      + '<path d="M2.5 4h11M6.5 4V2.8h3V4M4 4l.6 9.2h6.8L12 4" fill="none" stroke="currentColor" '
      + 'stroke-width="1.2" stroke-linecap="round"/></svg>';
    b.addEventListener('click', function () {
      b.disabled = true;
      setBundleCount(rows, 0);
    });
    return b;
  }

  /*
    A bundle that has lost a member is not a bundle.

    Shopify's own cart page, the Shop app, a recovery email link and Kaching's drawer can all
    remove a single line without going through our controls. When that happens the survivor is
    still stamped _ef_bundle, so it would keep rendering inside a box labelled "Fire Pit + Fuel
    Duo" while being one lonely fire pit — and, once our own discount rule exists, it would keep
    claiming a saving it no longer earns (Gabe, 2026-09-22: "if that does happen, you should
    lose the bundle pricing").

    Every bundle we build has at least two lines, so a group down to one is the detectable case.
    Blanking the properties dissolves it back into an ordinary line.
  */
  function dissolveOrphans(root) {
    var seen = {};
    var rows = root.querySelectorAll('tr[data-ef-bundle]');
    for (var i = 0; i < rows.length; i++) {
      var id = rows[i].getAttribute('data-ef-bundle');
      (seen[id] = seen[id] || []).push(rows[i]);
    }
    var updates = null;
    for (var id2 in seen) {
      if (seen[id2].length !== 1) continue;
      var key = seen[id2][0].getAttribute('data-ef-line-key');
      if (!key) continue;
      updates = updates || [];
      updates.push(key);
    }
    if (!updates) return false;

    // One request per orphan: /cart/change.js takes a single line at a time.
    updates.reduce(function (chain, key) {
      return chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, properties: { _ef_bundle: '', _ef_bundle_name: '', _ef_unit_qty: '', _ef_bundle_pos: '' } })
        });
      });
    }, Promise.resolve()).then(function () {
      document.dispatchEvent(new CustomEvent('ef:cart:updated'));
      window.location.reload();
    }).catch(function () {});
    return true;
  }

  function buildStepper(firstRow, rows) {
    if (!rows.length) return null;
    var count = bundleCount(firstRow);
    var wrap = document.createElement('span');
    wrap.className = 'ef-bundle-qty';

    function btn(label, next, disabled) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ef-bundle-qty__btn';
      b.textContent = label;
      b.setAttribute('aria-label', label === '\u2212' ? 'Remove one bundle' : 'Add one bundle');
      if (disabled) b.disabled = true;
      b.addEventListener('click', function () {
        wrap.classList.add('is-busy');
        setBundleCount(rows, next);
      });
      return b;
    }

    wrap.appendChild(btn('\u2212', count - 1, false));   // 0 removes the bundle entirely
    var n = document.createElement('span');
    n.className = 'ef-bundle-qty__n';
    n.textContent = String(count);
    wrap.appendChild(n);
    wrap.appendChild(btn('+', count + 1, false));
    return wrap;
  }

  function paintBundleHeaders(root) {
    if (!root) return;
    clearHeaders(root);

    var firsts = root.querySelectorAll('tr[data-ef-bundle-first]');
    for (var i = 0; i < firsts.length; i++) {
      var row = firsts[i];
      var name = row.getAttribute('data-ef-bundle-name');
      if (!name) continue;

      /*
        The header carries three things now, not just the name (Gabe, 2026-09-22: "it's unclear
        what is inside the Fire Pit Plus Fuel Duo bundle"):

          1. the bundle's name
          2. what is IN it, built from the group's own lines rather than from a label — so it
             cannot drift from what the cart actually holds
          3. ONE stepper for the whole group. A per-item stepper inside a bundle is a lie: you
             cannot have three refills in a two-refill Duo, and changing one line silently turns
             the bundle into loose items that no longer qualify for the discount.
      */
      var members = groupRows(root, row.getAttribute('data-ef-bundle'));
      var head = document.createElement('tr');
      head.className = HEAD_CLASS;
      head.setAttribute('role', 'row');

      var cell = document.createElement('td');
      cell.colSpan = columnCount(row);

      var title = document.createElement('span');
      title.className = 'ef-bundle-head__name';
      title.textContent = name;
      cell.appendChild(title);

      var contents = describe(members);
      if (contents) {
        var sub = document.createElement('span');
        sub.className = 'ef-bundle-head__contents';
        sub.textContent = contents;
        cell.appendChild(sub);
      }

      var controls = document.createElement('span');
      controls.className = 'ef-bundle-controls';
      var stepper = buildStepper(row, members);
      if (stepper) controls.appendChild(stepper);
      controls.appendChild(buildRemove(members));
      cell.appendChild(controls);

      head.appendChild(cell);
      row.parentNode.insertBefore(head, row);
    }
  }

  function run() {
    try {
      var drawer = document.getElementById('CartDrawer-CartItems');
      if (drawer && dissolveOrphans(drawer)) return;  // a reload is coming; don't paint twice
      paintBundleHeaders(drawer);
      paintBundleHeaders(document.getElementById('main-cart-items'));
      paintBundleHeaders(document.querySelector('.js-contents'));
    } catch (e) {
      /* never let cart decoration break the cart */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Dawn swaps cart markup wholesale on every quantity change, add and remove. Watching the
  // subtree is more reliable than guessing which events the theme and its apps emit.
  try {
    var target = document.querySelector('cart-drawer') || document.querySelector('.cart-items');
    if (target && window.MutationObserver) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          run();
        });
      }).observe(target, { childList: true, subtree: true });
    }
  } catch (e) {}

  ['cart:refresh', 'ef:cart:updated', 'ef-lp:cart-updated'].forEach(function (evt) {
    document.addEventListener(evt, run);
  });
})();


/* ----------------------------------------------------------------- pre-order dialog
   One delegated listener for every chip, bound to the document rather than to the buttons,
   because the drawer's markup is replaced wholesale on every cart write — a listener attached to
   a chip does not survive the next add. Native <dialog> carries the focus trap, Escape and the
   backdrop; we only decide when it opens.
*/
(function () {
  'use strict';
  function dialog() { return document.getElementById('EfPreorderDialog'); }

  document.addEventListener('click', function (e) {
    var open = e.target.closest && e.target.closest('[data-ef-preorder-open]');
    if (open) {
      var d = dialog();
      if (!d) return;
      e.preventDefault();
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');       // no <dialog> support: still readable, not broken
      return;
    }
    var close = e.target.closest && e.target.closest('[data-ef-preorder-close]');
    if (close) {
      var dd = dialog();
      if (!dd) return;
      if (typeof dd.close === 'function') dd.close();
      else dd.removeAttribute('open');
    }
  });

  // Clicking the backdrop closes it. <dialog> reports the click on itself, not on a child.
  document.addEventListener('click', function (e) {
    var d = dialog();
    if (d && e.target === d && typeof d.close === 'function') d.close();
  });
})();
